import React, { useEffect, useState, useMemo} from 'react';
import { debounce } from 'lodash';
// import { token } from '../../../../../env';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BASEAPIURL } from '../../env';

const Booking = () => {
  const [bookingData, setBookingData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [editingPlayerField, setEditingPlayerField] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch Booking Data (simplified)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASEAPIURL}/GetBookingGolft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify( "token" ),
        });
        const json = await res.json();
        const data = Array.isArray(json) ? json : json.data || [];
        const enriched = data.map(item => ({
          ...item,
          PlayerName_1: item.PlayerID_1 || '',
          PlayerName_2: item.PlayerID_2 || '',
          PlayerName_3: item.PlayerID_3 || '',
          PlayerName_4: item.PlayerID_4 || ''
        }));
        setBookingData(enriched);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  // Filtering
  const filteredData = useMemo(() => {
    let data = bookingData;
    if (searchTerm) {
      data = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (selectedDate) {
      data = data.filter(item => item.BookingDate?.startsWith(selectedDate));
    }
    if (statusFilter) {
      data = data.filter(item => item.StatusOfBooking?.toLowerCase() === statusFilter.toLowerCase());
    }
    return data;
  }, [bookingData, searchTerm, selectedDate, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  // Unique status for dropdown
  const uniqueStatuses = useMemo(() => [...new Set(bookingData.map(i => i.StatusOfBooking || '-'))], [bookingData]);

  // Editing handlers
  const handleEdit = item => {
    setEditingItem(item.BookID);
    setEditFormData({ ...item });
  };
  const cancelEdit = () => setEditingItem(null);
  const handleEditChange = e => {
    setEditFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const saveEdit = async () => {
    const updated = editFormData;
    if (!updated) return;
    const payload = {
      tokendata:"token",
      Book: {
        BookID: updated.BookID,
        BookingDate: updated.BookingDate,
        BookingSlot: updated.BookingSlot,
        SlotStatus: updated.SlotStatus,
        PlayerID_1: updated.PlayerID_1,
        PlayerID_2: updated.PlayerID_2,
        PlayerID_3: updated.PlayerID_3,
        PlayerID_4: updated.PlayerID_4,
        TypeOfBooking: 'Mannual',
        TypeOfGame: updated.GameType,
        StatusOfBooking: updated.StatusOfBooking,
        Is_BookingType: 'teeBooking'
      }
    };
    try {
      const res = await fetch(`${BASEAPIURL}/UpdateBookingGolf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.status) {
        setBookingData(prev =>
          prev.map(i => (i.BookID === editingItem ? { ...i, ...updated } : i))
        );
        setEditingItem(null);
      } else {
        alert('Failed to update booking');
      }
    } catch (e) {
      console.error(e);
    }
  };
  const handleDelete = bookId => {
    if (!window.confirm('Are you sure to delete this booking?')) return;
    setBookingData(prev => prev.filter(i => i.BookID !== bookId));
  };

  // Player modal functions
  const openPlayerModal = field => {
    setEditingPlayerField(field);
    setIsPlayerModalOpen(true);
  };
  const closePlayerModal = () => setIsPlayerModalOpen(false);
  const handlePlayerSelect = (id, name) => {
    setEditFormData(prev => ({
      ...prev,
      [editingPlayerField]: id,
      [`PlayerName_${editingPlayerField.split('_')[1]}`]: name,
    }));
    setIsPlayerModalOpen(false);
  };

  if (loading) return <div className="text-center my-4">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-3 mb-2">
          <input
            type="text"
            placeholder="Search..."
            className="form-control"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-2">
          <input
            type="date"
            className="form-control"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            {uniqueStatuses.map((status, idx) => (
              <option key={idx} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table view: visible on md+ */}
      <div className="table-responsive d-none d-md-block" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table className="table table-bordered table-sm">
          <thead className="table-light sticky-top">
            <tr>
              <th>BookID</th>
              {[1, 2, 3, 4].map(n => (
                <th key={n}>Player {n}</th>
              ))}
              <th>Booking Date</th>
              <th>Booking Slot</th>
              <th>Slot Status</th>
              <th>Booking Type</th>
              <th>Game Type</th>
              <th>Status Of Booking</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(item => {
              const isEditing = editingItem === item.BookID;
              return (
                <tr key={item.BookID}>
                  <td>{item.BookID}</td>

                  {[1, 2, 3, 4].map(n => (
                    <td key={n}>
                      {isEditing ? (
                        <input
                          readOnly
                          className="form-control form-control-sm"
                          style={{ cursor: 'pointer' }}
                          value={editFormData[`PlayerID_${n}`] || ''}
                          onClick={() => openPlayerModal(`PlayerID_${n}`)}
                        />
                      ) : (
                        item[`PlayerName_${n}`] || ''
                      )}
                    </td>
                  ))}

                  <td>{item.BookingDate}</td>
                  <td>{item.BookingSlot}</td>
                  <td>
                    {isEditing ? (
                      <input
                        name="SlotStatus"
                        className="form-control form-control-sm"
                        value={editFormData.SlotStatus || ''}
                        onChange={handleEditChange}
                      />
                    ) : (
                      item.SlotStatus
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        name="BookingType"
                        className="form-control form-control-sm"
                        value={editFormData.BookingType || ''}
                        onChange={handleEditChange}
                      />
                    ) : (
                      item.BookingType
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        name="GameType"
                        className="form-control form-control-sm"
                        value={editFormData.GameType || ''}
                        onChange={handleEditChange}
                      />
                    ) : (
                      item.GameType
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        name="StatusOfBooking"
                        className="form-select form-select-sm"
                        value={editFormData.StatusOfBooking || ''}
                        onChange={handleEditChange}
                      >
                        {uniqueStatuses.map((status, idx) => (
                          <option key={idx} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    ) : (
                      item.StatusOfBooking
                    )}
                  </td>
                  <td style={{ minWidth: 130 }}>
                    {isEditing ? (
                      <>
                        <button className="btn btn-success btn-sm me-1" onClick={saveEdit}>
                          Save
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-primary btn-sm me-1" onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.BookID)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Card view: visible on sm and xs */}
      <div className="d-md-none">
        {paginatedData.map(item => {
          const isEditing = editingItem === item.BookID;
          return (
            <div key={item.BookID} className="card mb-3 shadow-sm">
              <div className="card-body">
                <h5 className="card-title">Booking ID: {item.BookID}</h5>

                {[1, 2, 3, 4].map(n => (
                  <div key={n} className="mb-2">
                    <strong>Player {n}: </strong>
                    {isEditing ? (
                      <input
                        readOnly
                        className="form-control form-control-sm"
                        style={{ cursor: 'pointer' }}
                        value={editFormData[`PlayerID_${n}`] || ''}
                        onClick={() => openPlayerModal(`PlayerID_${n}`)}
                      />
                    ) : (
                      item[`PlayerName_${n}`] || '-'
                    )}
                  </div>
                ))}

                <div className="mb-2">
                  <strong>Booking Date: </strong>
                  {item.BookingDate}
                </div>

                <div className="mb-2">
                  <strong>Booking Slot: </strong>
                  {item.BookingSlot}
                </div>

                <div className="mb-2">
                  <strong>Slot Status: </strong>
                  {isEditing ? (
                    <input
                      name="SlotStatus"
                      className="form-control form-control-sm"
                      value={editFormData.SlotStatus || ''}
                      onChange={handleEditChange}
                    />
                  ) : (
                    item.SlotStatus
                  )}
                </div>

                <div className="mb-2">
                  <strong>Booking Type: </strong>
                  {isEditing ? (
                    <input
                      name="BookingType"
                      className="form-control form-control-sm"
                      value={editFormData.BookingType || ''}
                      onChange={handleEditChange}
                    />
                  ) : (
                    item.BookingType
                  )}
                </div>

                <div className="mb-2">
                  <strong>Game Type: </strong>
                  {isEditing ? (
                    <input
                      name="GameType"
                      className="form-control form-control-sm"
                      value={editFormData.GameType || ''}
                      onChange={handleEditChange}
                    />
                  ) : (
                    item.GameType
                  )}
                </div>

                <div className="mb-2">
                  <strong>Status Of Booking: </strong>
                  {isEditing ? (
                    <select
                      name="StatusOfBooking"
                      className="form-select form-select-sm"
                      value={editFormData.StatusOfBooking || ''}
                      onChange={handleEditChange}
                    >
                      {uniqueStatuses.map((status, idx) => (
                        <option key={idx} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  ) : (
                    item.StatusOfBooking
                  )}
                </div>

                <div>
                  {isEditing ? (
                    <>
                      <button className="btn btn-success btn-sm me-2" onClick={saveEdit}>
                        Save
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-primary btn-sm me-2" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.BookID)}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <nav aria-label="Page navigation" className="my-3">
        <ul className="pagination justify-content-center flex-wrap">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
              Previous
            </button>
          </li>
          {[...Array(totalPages).keys()].map(n => (
            <li key={n + 1} className={`page-item ${currentPage === n + 1 ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(n + 1)}>
                {n + 1}
              </button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
              Next
            </button>
          </li>
        </ul>
      </nav>

      {/* Player Modal */}
      {isPlayerModalOpen && (
        <div
          className="modal show fade d-block"
          tabIndex={-1}
          role="dialog"
          onClick={closePlayerModal}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div
            className="modal-dialog"
            role="document"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Player</h5>
                <button type="button" className="btn-close" onClick={closePlayerModal}></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Search players..."
                  value={playerSearchTerm}
                  onChange={e => {
                    const val = e.target.value;
                    setPlayerSearchTerm(val);
                    debouncedPlayerSearch(val);
                  }}
                  autoFocus
                />
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {playerSearchResults.length === 0 && (
                    <div className="text-muted">No results found.</div>
                  )}
                  {playerSearchResults.map(player => (
                    <div
                      key={player.MemID}
                      className="list-group-item list-group-item-action"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handlePlayerSelect(player.MemID, player.MemName)}
                    >
                      {player.MemName} (ID: {player.MemID})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Debounced player search function
  function debouncedPlayerSearch(query) {
    if (!query) {
      setPlayerSearchResults([]);
      return;
    }
    debounce(async (searchText) => {
      try {
        const response = await fetch(`${BASEAPIURL}/MemberSearchedList`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // body: JSON.stringify({ Search: searchText, "token" }),
        });
        const data = await response.json();
        if (Array.isArray(data)) {
          setPlayerSearchResults(data);
        } else if (data?.data) {
          setPlayerSearchResults(data.data);
        } else {
          setPlayerSearchResults([]);
        }
      } catch {
        setPlayerSearchResults([]);
      }
    }, 300)(query);
  }
};

export default Booking;

